import { LineItemDtoType } from "src/app/core/models/http/treatment/insurance-policy-benefits/line-items/line-item-dto-type";
import { DhmoLineItemDto } from "src/app/core/models/http/treatment/insurance-policy-benefits/line-items/dhmo-line-item-dto";
import { TreatmentPlanBenefitDto } from "../../../../core/models/http/treatment/treatment-plan-benefits/treatment-plan-benefit-dto";
import { LineItemType } from "../../../../core/models/line-item-type";
import { FeeNode } from "../fee-node";
import { ExpenseFee } from "../fees/models/expense-fee";
import { Fee } from "../fees/models/fee";
import { Procedure } from "../procedures/procedure";
import { InsurancePolicyBenefit } from "./insurance-policy-benefit";
import { LineItemModelType } from "./line-item-model-type";
import { DhmoLineItem } from "./dhmo-line-item";

export const flattenFees = (fees: LineItemModelType[]): LineItemModelType[] => {
  const result = [];
  for (const fee of fees) {
    result.push(fee);
    result.push(...flattenFees(fee.lineItems));
  }

  return result;
};

export const mapLineItems = (
  fees: LineItemDtoType[],
  parent: LineItemModelType = null
): LineItemModelType[] => {
  const result: LineItemModelType[] = [];

  for (const fee of fees) {
    const lineItem: LineItemModelType = {
      lineItems: [],
      id: fee.Id,
      practiceAmount: fee.PracticeAmount,
      contractedAmount: fee.ContractedAmount,
      description: fee.Description,
      feeId: fee.FeeId,
      feeType: fee.FeeType,
      lineItemType: fee.LineItemType,
      included: fee.Included,
      networkDiscount: fee.NetworkDiscount,
      parent,
      procedure: fee.Procedure
        ? {
            code: fee.Procedure.Code,
            id: fee.Procedure.Id,
          }
        : null,
      insuranceBenefit: (fee as DhmoLineItemDto).InsuranceBenefit,
      patientCopay: (fee as DhmoLineItemDto).PatientCopay,
      networkType: fee.NetworkType,
      insurancePolicyBenefitId: fee.InsurancePolicyBenefitId,
    };

    lineItem.lineItems = mapLineItems(fee.LineItems, lineItem);

    result.push(lineItem);
  }

  return result;
};

/** 
 * A projection function that converts the list of policies on a plan to a standalone list of benefits. 
 * 
 * @prop b A treatment plan as represented by the HTTP API.
 * @returns A list benefits from various insurance providers with benefit details.
 */
 export const mapTreatmentPlanBenefits = (
  b: TreatmentPlanBenefitDto
): InsurancePolicyBenefit[] => {
  return b.InsurancePolicyBenefits.map((ipb) => {
    return {
      benefit: ipb.Benefit,
      estimatedBenefit: ipb.EstimatedBenefit,
      flattenedFees: [],
      id: ipb.Id,
      insurancePolicyPatientEligibilityId:
        ipb.InsurancePolicyPatientEligibilityId,
      lineItems: [],
      networkDiscount: ipb.NetworkDiscount,
      treatmentPlanId: b.TreatmentPlanId,
    };
  });
};

export const mergeFees = (
  standardFees: Fee[],
  fees: LineItemModelType[],
  procedures: Procedure[]
): FeeNode[] => {
  return fees
    .map((e) => {
      const fee = standardFees.find((f) => f.id === e.feeId);

      if (!fee && e.feeId) {
        return null;
      }

      if (fee) {
        let practiceAmount = fee.practiceAmount;
        if (fee.feeType === "Treatment") {
          practiceAmount -= standardFees.reduce(
            (pv, cv) =>
              pv +
              (cv.feeType === "Expense" && cv.included ? cv.practiceAmount : 0),
            0
          );
        }
        return {
          feeType: fee.feeType,
          lineItemType: e.lineItemType,
          practiceAmount,
          description: fee.description,
          networkDiscount: e.networkDiscount,
          children: mergeFees(standardFees, e.lineItems, procedures),
          contractedRate: e.contractedAmount,
          id: fee.id,
          included: (fee as ExpenseFee).included,
          lineItemId: e.id,
          procedure: fee.procedureId
            ? procedures.find((p) => p.id === fee.procedureId)
            : null,
          patientCopay: (e as DhmoLineItem).patientCopay,
          insuranceBenefit: (e as DhmoLineItem).insuranceBenefit,
        };
      }

      return {
        feeType: null,
        lineItemType: e.lineItemType,
        practiceAmount: e.practiceAmount,
        description: e.description,
        networkDiscount: e.networkDiscount,
        children: mergeFees(standardFees, e.lineItems, procedures),
        contractedRate: e.contractedAmount,
        id: e.feeId,
        included: e.included,
        lineItemId: e.id,
        procedure: e.procedure
          ? { id: e.procedure.id, code: e.procedure.code }
          : null,
        patientCopay: (e as DhmoLineItem).patientCopay,
        insuranceBenefit: (e as DhmoLineItem).insuranceBenefit,
      };
    })
    .filter((f) => !!f)
    .sort((a, b) => {
      if (a.lineItemType === LineItemType.Summary) {
        return -1;
      }
      if (b.lineItemType === LineItemType.Summary) {
        return 1;
      }
      if (a.feeType === "TreatmentOption") {
        return 1;
      }
      if (b.feeType === "TreatmentOption") {
        return -1;
      }
      if (a.feeType === "Treatment") {
        return -1;
      }
      if (b.feeType === "Treatment") {
        return 1;
      }
      return b.description.localeCompare(a.description);
    });
};
